import React, { useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Upload, Check, FileText, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { COUNTRIES } from "@/data/constants";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { useSettings } from "@/App";
import { Check as CheckIcon, ShieldCheck, RefreshCcw } from "lucide-react";

const SOURCE_TYPES = ["Property / Real Estate", "Materials", "Equipment", "Commercial Opportunity", "Other"];

const Field = ({ label, children }) => (
  <div>
    <label className="font-sans text-[11px] tracking-luxe uppercase text-softgrey/70">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);
const inputCls = "w-full border-b border-gold/25 bg-transparent py-2.5 font-sans text-sm text-warmwhite outline-none placeholder:text-softgrey/40 focus:border-gold transition-colors";
const Text = (p) => <input {...p} className={inputCls} />;
const Area = (p) => <textarea {...p} className={inputCls} />;
const Select = ({ options, ...p }) => (
  <select {...p} className={`${inputCls} [&>option]:bg-obsidian`}>
    <option value="">Select…</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const PROPERTY_TYPES = ["Residential", "Commercial", "Land", "Development", "Hospitality", "Mixed Use"];
const DEAL_STRATEGIES = ["BTL", "BRRR", "Airbnb / Serviced Accommodation", "Off-Plan", "Development", "Flip / Value Add", "Land", "Capital Growth", "JV / Partnership", "Other"];
const FINANCE = ["Cash", "Mortgage", "Finance", "JV", "Flexible"];
const RISK = ["Conservative", "Balanced", "Aggressive"];
const RECURRENCE = ["One-Off", "Recurring"];

function ProcessSections({ process = {} }) {
  const steps = process.steps || [];
  const commitment = process.commitment || [];
  return (
    <div className="mt-16 space-y-16">
      {steps.length > 0 && (
        <div>
          <Eyebrow>The Process</Eyebrow>
          <h2 className="mt-5 font-serif text-2xl text-warmwhite lg:text-4xl">How Our Bespoke Sourcing Works</h2>
          <p className="mt-3 font-sans text-sm text-softgrey">Four deliberate steps from your brief to a deal worth signing.</p>
          <div className="mt-10 grid gap-px overflow-hidden border border-gold/15 bg-gold/15 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div className="h-full bg-obsidian p-6" data-testid={`process-step-${s.n}`}>
                  <div className="font-mono text-2xl text-gold/40">{s.n}</div>
                  <h3 className="mt-4 font-serif text-lg text-warmwhite">{s.t}</h3>
                  <p className="mt-2 font-sans text-xs leading-relaxed text-softgrey">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {commitment.length > 0 && (
          <div className="border border-gold/15 bg-obsidian p-8">
            <div className="flex items-center gap-3"><ShieldCheck size={18} className="text-gold" /><h3 className="font-serif text-xl text-warmwhite">Every Deal Is…</h3></div>
            <ul className="mt-5 space-y-3">
              {commitment.map((c, i) => (
                <li key={i} className="flex gap-3 font-sans text-sm text-softgrey"><CheckIcon size={15} className="mt-0.5 shrink-0 text-gold/70" /> {c}</li>
              ))}
            </ul>
            <p className="mt-5 font-sans text-xs italic text-softgrey/60">Only opportunities meeting both your standards and ours are presented.</p>
          </div>
        )}

        <div className="border border-gold/20 bg-seablue/20 p-8">
          <div className="font-sans text-[11px] tracking-luxe uppercase text-gold">A Structured, Risk-Controlled Process</div>
          <div className="mt-4 flex items-end gap-3">
            <div className="font-serif text-5xl text-gradient-gold">{process.deposit || "—"}</div>
            <div className="pb-1 font-sans text-xs text-softgrey">{process.deposit_note || "Holding deposit"}</div>
          </div>
          <p className="mt-3 font-sans text-sm text-softgrey">Activates a {process.window || "dedicated"} dedicated search.</p>
          {process.refund && (
            <div className="mt-5 flex gap-3 border-t border-gold/10 pt-5">
              <RefreshCcw size={16} className="mt-0.5 shrink-0 text-gold" />
              <p className="font-sans text-xs leading-relaxed text-softgrey">{process.refund}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BespokeSourcing() {
  const { settings } = useSettings();
  const process = settings?.process || {};
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [contact, setContact] = useState({ full_name: "", email: "", phone: "", country: "", profile_type: "", company_name: "" });
  const [sourceType, setSourceType] = useState("");
  const [details, setDetails] = useState({});
  const [docs, setDocs] = useState([]);

  const setD = (k, v) => setDetails((p) => ({ ...p, [k]: v }));
  const totalSteps = 3;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDocs((p) => [...p, { url: data.url, filename: data.filename }]);
      }
      toast.success("Document uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const canNext = () => {
    if (step === 1) return contact.full_name && contact.email && contact.profile_type;
    if (step === 2) return !!sourceType;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/mandates", { source_type: sourceType, contact, details, documents: docs });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const isProperty = sourceType === "Property / Real Estate";
  const isMatEq = sourceType === "Materials" || sourceType === "Equipment";

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-6 pt-24" data-testid="mandate-confirmation">
        <div className="max-w-lg border border-gold/25 bg-seablue/20 p-12 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold"><Check className="text-gold" /></span>
          <h1 className="mt-6 font-serif text-3xl text-warmwhite">Mandate Received</h1>
          <p className="mt-2 font-mono text-sm text-gold">Ref: {done.ref}</p>
          <p className="mt-5 font-sans text-sm leading-relaxed text-softgrey">{done.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian px-6 pt-32 pb-24 lg:px-10" data-testid="bespoke-page">
      <div className="mx-auto max-w-5xl">
        <Eyebrow>Bespoke Deal Sourcing</Eyebrow>
        <h1 className="mt-5 font-serif text-4xl leading-tight text-warmwhite lg:text-6xl">Tell us what you are looking for.</h1>
        <p className="mt-5 max-w-2xl font-sans text-base leading-relaxed text-softgrey">The opportunities you see publicly are a fraction of what passes through our desk. Share your acquisition criteria and we'll surface matches from our private pipeline. <span className="text-gold">Confidential · Curated · Investor-Led</span></p>
        <ProcessSections process={process} />
      </div>

      <div className="mx-auto mt-20 max-w-2xl">
        <div className="text-center font-sans text-[11px] tracking-luxe uppercase text-gold">↓ Start Your Brief</div>

        {/* progress */}
        <div className="mt-10 flex items-center gap-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-0.5 flex-1 transition-colors ${n <= step ? "bg-gold" : "bg-gold/15"}`} />
          ))}
        </div>
        <div className="mt-3 font-sans text-[11px] tracking-luxe uppercase text-softgrey/60">Step {step} / {totalSteps}</div>

        <div className="mt-10 border border-gold/15 bg-obsidian p-8 lg:p-10">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              {step === 1 && (
                <div className="space-y-7" data-testid="step-about-you">
                  <h2 className="font-serif text-2xl text-gold">About You</h2>
                  <Field label="Full Name"><Text data-testid="f-name" value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} placeholder="Your name" /></Field>
                  <Field label="Email"><Text data-testid="f-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="you@email.com" /></Field>
                  <Field label="Phone / WhatsApp"><Text data-testid="f-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="+44 …" /></Field>
                  <Field label="Country of Residence"><Select data-testid="f-country" options={COUNTRIES} value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} /></Field>
                  <Field label="You are a…"><Select data-testid="f-profile" options={["Individual", "Company", "Investor"]} value={contact.profile_type} onChange={(e) => setContact({ ...contact, profile_type: e.target.value })} /></Field>
                  {contact.profile_type === "Company" && (
                    <Field label="Company Name"><Text data-testid="f-company" value={contact.company_name} onChange={(e) => setContact({ ...contact, company_name: e.target.value })} placeholder="Company" /></Field>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="step-what">
                  <h2 className="font-serif text-2xl text-gold">What would you like us to source?</h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {SOURCE_TYPES.map((t) => (
                      <button key={t} onClick={() => setSourceType(t)} data-testid={`source-${t}`}
                              className={`border p-4 text-left font-sans text-sm transition-colors ${sourceType === t ? "border-gold bg-gold/10 text-warmwhite" : "border-gold/20 text-softgrey hover:border-gold/50"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-7" data-testid="step-details">
                  <h2 className="font-serif text-2xl text-gold">Your Requirements</h2>

                  {isProperty && (
                    <>
                      <div className="grid gap-6 sm:grid-cols-2">
                        <Field label="Preferred Country"><Select options={COUNTRIES} value={details.country || ""} onChange={(e) => setD("country", e.target.value)} /></Field>
                        <Field label="Preferred City / Region"><Text value={details.city || ""} onChange={(e) => setD("city", e.target.value)} placeholder="e.g. Accra" /></Field>
                        <Field label="Property Type"><Select options={PROPERTY_TYPES} value={details.property_type || ""} onChange={(e) => setD("property_type", e.target.value)} /></Field>
                        <Field label="Deal Type / Strategy"><Select options={DEAL_STRATEGIES} value={details.strategy || ""} onChange={(e) => setD("strategy", e.target.value)} /></Field>
                        <Field label="Budget Range"><Text value={details.budget || ""} onChange={(e) => setD("budget", e.target.value)} placeholder="e.g. USD 150k–300k" /></Field>
                        <Field label="Available Deposit / Capital"><Text value={details.capital || ""} onChange={(e) => setD("capital", e.target.value)} placeholder="e.g. USD 50k" /></Field>
                        <Field label="Funding"><Select options={FINANCE} value={details.finance || ""} onChange={(e) => setD("finance", e.target.value)} /></Field>
                        <Field label="Target Yield"><Text value={details.yield || ""} onChange={(e) => setD("yield", e.target.value)} placeholder="e.g. 8%" /></Field>
                        <Field label="Capital Growth Objective"><Text value={details.growth || ""} onChange={(e) => setD("growth", e.target.value)} placeholder="e.g. 15% CAGR" /></Field>
                        <Field label="Preferred Holding Period"><Text value={details.holding || ""} onChange={(e) => setD("holding", e.target.value)} placeholder="e.g. 5 years" /></Field>
                        <Field label="Bedrooms / Unit Type"><Text value={details.units || ""} onChange={(e) => setD("units", e.target.value)} placeholder="e.g. 2 bed" /></Field>
                        <Field label="Timeline to Invest"><Text value={details.timeline || ""} onChange={(e) => setD("timeline", e.target.value)} placeholder="e.g. 3 months" /></Field>
                        <Field label="Risk Appetite"><Select options={RISK} value={details.risk || ""} onChange={(e) => setD("risk", e.target.value)} /></Field>
                      </div>
                      <Field label="Specific Requirements"><Area rows={2} value={details.requirements || ""} onChange={(e) => setD("requirements", e.target.value)} /></Field>
                      <Field label="Exclusions"><Area rows={2} value={details.exclusions || ""} onChange={(e) => setD("exclusions", e.target.value)} /></Field>
                    </>
                  )}

                  {isMatEq && (
                    <>
                      <div className="grid gap-6 sm:grid-cols-2">
                        <Field label="Product / Equipment Required"><Text value={details.product || ""} onChange={(e) => setD("product", e.target.value)} /></Field>
                        <Field label="Quantity"><Text value={details.quantity || ""} onChange={(e) => setD("quantity", e.target.value)} /></Field>
                        <Field label="Preferred Manufacturer / Brand"><Text value={details.brand || ""} onChange={(e) => setD("brand", e.target.value)} /></Field>
                        <Field label="Country of Delivery"><Select options={COUNTRIES} value={details.delivery_country || ""} onChange={(e) => setD("delivery_country", e.target.value)} /></Field>
                        <Field label="Delivery Location"><Text value={details.delivery_location || ""} onChange={(e) => setD("delivery_location", e.target.value)} /></Field>
                        <Field label="Required Delivery Date"><Text value={details.delivery_date || ""} onChange={(e) => setD("delivery_date", e.target.value)} placeholder="e.g. Q3 2026" /></Field>
                        <Field label="Target Budget"><Text value={details.budget || ""} onChange={(e) => setD("budget", e.target.value)} /></Field>
                        <Field label="Preferred Country of Origin"><Select options={COUNTRIES} value={details.origin || ""} onChange={(e) => setD("origin", e.target.value)} /></Field>
                        <Field label="Incoterm Preference"><Text value={details.incoterm || ""} onChange={(e) => setD("incoterm", e.target.value)} placeholder="e.g. CIF, FOB" /></Field>
                        <Field label="Requirement Type"><Select options={RECURRENCE} value={details.recurrence || ""} onChange={(e) => setD("recurrence", e.target.value)} /></Field>
                      </div>
                      <Field label="Specification"><Area rows={2} value={details.specification || ""} onChange={(e) => setD("specification", e.target.value)} /></Field>
                      <Field label="Certification / Standards Requirements"><Area rows={2} value={details.certification || ""} onChange={(e) => setD("certification", e.target.value)} /></Field>
                      <Field label="Additional Requirements"><Area rows={2} value={details.additional || ""} onChange={(e) => setD("additional", e.target.value)} /></Field>
                    </>
                  )}

                  {(sourceType === "Commercial Opportunity" || sourceType === "Other") && (
                    <>
                      <div className="grid gap-6 sm:grid-cols-2">
                        <Field label="Opportunity Sought"><Text value={details.opportunity || ""} onChange={(e) => setD("opportunity", e.target.value)} /></Field>
                        <Field label="Sector"><Text value={details.sector || ""} onChange={(e) => setD("sector", e.target.value)} /></Field>
                        <Field label="Country / Market"><Select options={COUNTRIES} value={details.market || ""} onChange={(e) => setD("market", e.target.value)} /></Field>
                        <Field label="Budget"><Text value={details.budget || ""} onChange={(e) => setD("budget", e.target.value)} /></Field>
                        <Field label="Transaction Type"><Text value={details.transaction || ""} onChange={(e) => setD("transaction", e.target.value)} placeholder="e.g. Acquisition, JV" /></Field>
                        <Field label="Timeline"><Text value={details.timeline || ""} onChange={(e) => setD("timeline", e.target.value)} /></Field>
                        <Field label="Required Return / Value Parameters"><Text value={details.return_params || ""} onChange={(e) => setD("return_params", e.target.value)} /></Field>
                      </div>
                      <Field label="Commercial Objectives"><Area rows={2} value={details.objectives || ""} onChange={(e) => setD("objectives", e.target.value)} /></Field>
                      <Field label="Additional Information"><Area rows={2} value={details.additional || ""} onChange={(e) => setD("additional", e.target.value)} /></Field>
                    </>
                  )}

                  {/* document upload */}
                  <div>
                    <label className="font-sans text-[11px] tracking-luxe uppercase text-softgrey/70">Specification / BOQ / Documents (optional)</label>
                    <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 border border-dashed border-gold/30 py-5 font-sans text-sm text-softgrey transition-colors hover:border-gold/60" data-testid="f-upload">
                      <Upload size={16} className="text-gold" /> {uploading ? "Uploading…" : "Upload documents"}
                      <input type="file" multiple className="hidden" onChange={upload} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
                    </label>
                    {docs.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {docs.map((d, i) => (
                          <div key={i} className="flex items-center justify-between border border-gold/15 px-3 py-2">
                            <span className="flex items-center gap-2 font-sans text-xs text-warmwhite"><FileText size={13} className="text-gold" /> {d.filename}</span>
                            <button onClick={() => setDocs(docs.filter((_, j) => j !== i))} className="text-softgrey hover:text-warmwhite"><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* nav */}
          <div className="mt-10 flex items-center justify-between">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} data-testid="step-back" className="flex items-center gap-2 font-sans text-sm text-softgrey hover:text-warmwhite">
                <ArrowLeft size={15} /> Back
              </button>
            ) : <span />}

            {step < totalSteps ? (
              <button onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete the required fields")} data-testid="step-continue"
                      className={`flex items-center gap-2 px-7 py-3 font-sans text-sm font-medium tracking-wide transition-all ${canNext() ? "bg-gold text-obsidian hover:brightness-110" : "bg-gold/30 text-obsidian/60"}`}>
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="submit-mandate"
                      className="flex items-center gap-2 bg-gold px-7 py-3 font-sans text-sm font-medium tracking-wide text-obsidian transition-all hover:brightness-110 disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit Sourcing Mandate"} <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
